import { hasPermission } from '@/hooks/useBackendMenu'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import {
    JornadaMensal,
    espelhoPonto,
    funcionario as funcionarioApi,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import type { EdicaoEspelhoOptions } from './columns'
import {
    ModalHoraEdicao,
    ModalHorasExtrasEdicao,
} from './modals/EditarDiaEspelho'
import { invalidateDigitacaoQueries } from './pages/useDigitacaoData'

type LinhaJornada = JornadaMensal & { id: number | string }

type CampoHora = 'horasTrabalhadas' | 'faltas'

type EdicaoHora = { row: LinhaJornada; campo: CampoHora }

type EdicaoPendente = {
    row: LinhaJornada
    valores: Partial<{
        horasTrabalhadas: string
        faltas: string
        horasExtras: Record<string, string>
    }>
}

/** Rótulos de cada campo de hora editável por digitação */
const CONFIG_CAMPO: Record<CampoHora, { titulo: string; campoLabel: string }> =
    {
        horasTrabalhadas: {
            titulo: 'Editar horas trabalhadas',
            campoLabel: 'horas trabalhadas',
        },
        faltas: { titulo: 'Editar faltas', campoLabel: 'faltas' },
    }

const formatarDia = (dia: string) => dayjs.utc(dia).format('ddd DD/MM')

/**
 * Encapsula a edição por digitação de um dia do espelho na tela de Digitação
 * (views mensal e diário): abre modais de horas trabalhadas, faltas e horas
 * extras por porcentagem, todos exigindo justificativa, consumindo o endpoint
 * `PATCH /espelho-ponto/:funcionarioId/dias`. No mensal o funcionário vem do
 * filtro; no diário cada linha é um funcionário e a edição usa o da linha.
 */
export function useEdicaoEspelho(view: 'mensal' | 'diario') {
    const funcionarioId = useStore(state => state.employeeId)
    const competencia = useStore(state => state.competencia)
    const competenciaAtual = useStore(state => state.company?.competenciaAtual)

    const [edicaoHora, setEdicaoHora] = useState<EdicaoHora | null>(null)
    const [linhaHorasExtras, setLinhaHorasExtras] =
        useState<LinhaJornada | null>(null)
    const [salvando, setSalvando] = useState(false)

    const idRegras =
        view === 'mensal'
            ? funcionarioId
            : (linhaHorasExtras?.funcionarioId ?? 0)
    const { data: funcionario } = funcionarioApi.useFetchById(idRegras, {
        enabled: idRegras > 0,
    })

    const podeEditar =
        hasPermission(['update', 'EdiçãoManualDoEspelhoDoPonto']) &&
        (view === 'diario' || funcionarioId > 0) &&
        (!competenciaAtual ||
            !competencia ||
            String(competencia) >= competenciaAtual)

    const porcentagens = useMemo(
        () =>
            (funcionario?.RegrasCalculo?.Porcentagens ?? [])
                .map(item => String(item.porcentagem))
                .filter(Boolean),
        [funcionario],
    )

    const compensacoes = useMemo(
        () =>
            Object.fromEntries(
                (funcionario?.RegrasCalculo?.Porcentagens ?? []).map(item => [
                    String(item.porcentagem),
                    Number(item.compensacao),
                ]),
            ),
        [funcionario],
    )

    async function salvarEdicao(
        { row, valores }: EdicaoPendente,
        justificativa: string,
    ) {
        if (!competencia) {
            toast.error('Competência não identificada')
            return
        }
        const alvoFuncionarioId =
            view === 'mensal' ? funcionarioId : row.funcionarioId
        if (!alvoFuncionarioId) {
            toast.error('Funcionário não identificado')
            return
        }
        setSalvando(true)
        try {
            await espelhoPonto.editarDia(alvoFuncionarioId, {
                competencia: String(competencia),
                data: dayjs.utc(row.dia).format('YYYY-MM-DD'),
                horasTrabalhadas:
                    valores.horasTrabalhadas ?? row.horasTrabalhadas ?? '00:00',
                faltas: valores.faltas ?? row.faltas ?? '00:00',
                horasExtras:
                    valores.horasExtras ??
                    Object.fromEntries(
                        Object.entries(row.porcentagemHrsExtras ?? {}).map(
                            ([porcentagem, valor]) => [
                                porcentagem,
                                String(valor),
                            ],
                        ),
                    ),
                justificativa,
            })
            toast.success('Dia do espelho atualizado')
        } catch (error) {
            toast.error(
                (error as Error)?.message || 'Erro ao editar o dia do espelho',
            )
        } finally {
            setSalvando(false)
            setEdicaoHora(null)
            setLinhaHorasExtras(null)
            await invalidateDigitacaoQueries()
        }
    }

    const edicao: EdicaoEspelhoOptions | undefined = podeEditar
        ? {
              habilitada: true,
              onEditarHora: (row, campo) =>
                  setEdicaoHora({ row: row as LinhaJornada, campo }),
              onEditarHorasExtras: row =>
                  setLinhaHorasExtras(row as LinhaJornada),
          }
        : undefined

    const Modais = (
        <>
            {!!edicaoHora && (
                <ModalHoraEdicao
                    titulo={CONFIG_CAMPO[edicaoHora.campo].titulo}
                    campoLabel={CONFIG_CAMPO[edicaoHora.campo].campoLabel}
                    diaLabel={formatarDia(edicaoHora.row.dia)}
                    valorAtual={edicaoHora.row[edicaoHora.campo] ?? '00:00'}
                    justificativaInicial={edicaoHora.row.justificativa}
                    isLoading={salvando}
                    onClose={() => setEdicaoHora(null)}
                    onSubmit={({ valor, justificativa }) =>
                        salvarEdicao(
                            {
                                row: edicaoHora.row,
                                valores: { [edicaoHora.campo]: valor },
                            },
                            justificativa,
                        )
                    }
                />
            )}
            {!!linhaHorasExtras && (
                <ModalHorasExtrasEdicao
                    diaLabel={formatarDia(linhaHorasExtras.dia)}
                    porcentagens={
                        porcentagens.length
                            ? porcentagens
                            : Object.keys(
                                  linhaHorasExtras.porcentagemHrsExtras ?? {},
                              )
                    }
                    compensacoes={compensacoes}
                    valoresIniciais={Object.fromEntries(
                        Object.entries(
                            linhaHorasExtras.porcentagemHrsExtras ?? {},
                        ).map(([k, v]) => [k, String(v)]),
                    )}
                    justificativaInicial={linhaHorasExtras.justificativa}
                    isLoading={salvando}
                    onClose={() => setLinhaHorasExtras(null)}
                    onSubmit={({ horasExtras, justificativa }) =>
                        salvarEdicao(
                            { row: linhaHorasExtras, valores: { horasExtras } },
                            justificativa,
                        )
                    }
                />
            )}
        </>
    )

    return { edicao, Modais }
}
